import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const KINDS = [["appointment", "Appointment"], ["break", "Break"], ["holiday", "Holiday"], ["sickness", "Sickness"], ["other", "Other / closed"], ["extra", "Extra working hours"]];

export default function BlockOutDialog({ open, onClose, onSaved, onBook, staff, initial, ownerMode = true }) {
  const [form, setForm] = useState({
    staff_id: initial.staff_id || ((initial.kind === "appointment" || initial.kind === "extra" || !ownerMode) ? staff[0]?.id : "salon"), start_date: initial.start_date, end_date: initial.end_date || initial.start_date,
    all_day: initial.all_day ?? true, start: initial.start || "09:00", end: initial.end || "17:00", reason: "", kind: initial.kind || "holiday",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const extra = form.kind === "extra";
  const booking = form.kind === "appointment";
  const kinds = (ownerMode ? KINDS : KINDS.filter(([k]) => k === "extra" || k === "appointment")).filter(([k]) => k !== "appointment" || onBook);
  const book = () => onBook({ date: form.start_date, start: form.all_day ? "09:00" : form.start, staff_id: form.staff_id === "salon" ? staff[0]?.id : form.staff_id, status: "booked" });

  const setKind = (kind) => setForm((f) => ({
    ...f, kind,
    all_day: kind === "extra" || kind === "appointment" ? false : f.all_day,
    staff_id: (kind === "extra" || kind === "appointment") && f.staff_id === "salon" ? staff[0]?.id : f.staff_id,
  }));

  const save = async () => {
    setSaving(true); setError("");
    const payload = { ...form, staff_id: form.staff_id === "salon" ? null : form.staff_id };
    if (payload.all_day) { delete payload.start; delete payload.end; }
    try {
      await api.post("/blockouts", payload);
      toast.success(extra ? "Extra hours added" : "Time blocked out");
      onSaved(); onClose();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="blockout-dialog">
        <DialogHeader><DialogTitle>{booking ? "Book this time" : extra ? "Add extra working hours" : "Block out time"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>What for</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {kinds.map(([k, label]) => (
                <button key={k} type="button" data-testid={`blockout-kind-${k}`} onClick={() => setKind(k)}
                  className={`px-3 h-9 rounded-full border text-sm transition-colors ${form.kind === k ? (k === "extra" ? "bg-emerald-700 border-emerald-700 text-white" : "bg-stone-900 border-stone-900 text-white") : "bg-white border-stone-300"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div><Label>Who</Label>
            <Select value={form.staff_id} onValueChange={(v) => setForm((f) => ({ ...f, staff_id: v }))}>
              <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="blockout-who"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ownerMode && !extra && !booking && <SelectItem value="salon" data-testid="blockout-who-salon">Whole salon (closed)</SelectItem>}
                {staff.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`blockout-who-${s.id}`}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {booking ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" data-testid="blockout-start-date" value={form.start_date} onChange={set("start_date")} className="mt-1.5 h-11 rounded-lg" /></div>
              <div><Label>Start time</Label><Input type="time" data-testid="blockout-start-time" value={form.start} onChange={set("start")} className="mt-1.5 h-11 rounded-lg" /></div>
            </div>
          ) : (<>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From</Label><Input type="date" data-testid="blockout-start-date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value, end_date: f.end_date < e.target.value ? e.target.value : f.end_date }))} className="mt-1.5 h-11 rounded-lg" /></div>
            <div><Label>To</Label><Input type="date" data-testid="blockout-end-date" min={form.start_date} value={form.end_date} onChange={set("end_date")} className="mt-1.5 h-11 rounded-lg" /></div>
          </div>
          {!extra && <div className="flex items-center justify-between"><Label>All day</Label><Switch data-testid="blockout-all-day" checked={form.all_day} onCheckedChange={(v) => setForm((f) => ({ ...f, all_day: v }))} /></div>}
          {!form.all_day && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start time</Label><Input type="time" data-testid="blockout-start-time" value={form.start} onChange={set("start")} className="mt-1.5 h-11 rounded-lg" /></div>
              <div><Label>End time</Label><Input type="time" data-testid="blockout-end-time" value={form.end} onChange={set("end")} className="mt-1.5 h-11 rounded-lg" /></div>
            </div>
          )}
          <div><Label>Note (optional)</Label><Input data-testid="blockout-reason" value={form.reason} onChange={set("reason")} maxLength={120} placeholder={extra ? "e.g. Late opening for Christmas" : "e.g. Summer holiday, training"} className="mt-1.5 h-11 rounded-lg" /></div>
          </>)}
          {booking ? <p className="text-xs text-stone-500">Next you'll choose the client and treatment.</p> : <p className="text-xs text-stone-500">{extra ? "Clients can book online in these extra hours, every day in the date range." : "Clients can't book online in blocked time. It shows as closed in your diary."}</p>}
          {error && <p data-testid="blockout-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full">Cancel</Button>
          {booking ? <Button data-testid="blockout-book-button" onClick={book} disabled={!form.start_date || !form.start} className="rounded-full h-11 px-5 bg-stone-900 hover:bg-stone-800">Continue to booking</Button> :
          <Button data-testid="blockout-save-button" onClick={save} disabled={saving || !form.start_date || !form.end_date} className={`rounded-full h-11 px-5 ${extra ? "bg-emerald-700 hover:bg-emerald-800" : "bg-stone-900 hover:bg-stone-800"}`}>{saving ? "Saving…" : extra ? "Add hours" : "Block out"}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
